import { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "../lib/api";
import Topbar from "../components/Topbar";
import {
  AlertTriangle, Database, DownloadCloud, HardDriveDownload, KeyRound,
  Loader2, RefreshCw, Trash2, UploadCloud, Copy, CheckCircle2, Layers,
  ShieldAlert, ArrowLeftRight, ChevronDown, ListChecks, ClipboardCheck,
  XCircle, Wrench, Eye, EyeOff, Sparkles, ShieldCheck, Info,
} from "lucide-react";
import { toast } from "sonner";
import MigrationRepairStorage from "./MigrationRepairStorage";

const ORIGIN = API_BASE.replace(/\/api$/, "");

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
};

export default function AdminMigration() {
  const [token, setToken] = useState("");
  const [tokenError, setTokenError] = useState(null);
  const [info, setInfo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [mode, setMode] = useState("old"); // "old" (exportar) | "new" (importar)
  const [migrationMode, setMigrationMode] = useState(null); // franja: true/false/null(desconocido)

  // setup token
  const [newToken, setNewToken] = useState("");
  const [savingToken, setSavingToken] = useState(false);
  const [showResetToken, setShowResetToken] = useState(false);
  const [newTokenVisible, setNewTokenVisible] = useState(true);

  // backfill manual (herramienta de reparación)
  const [backfill, setBackfill] = useState(null);
  const pollRef = useRef(null);

  // ── Modo cuenta VIEJA: exportar todo en un solo botón ──────────────────────
  const [exportingAll, setExportingAll] = useState(false);
  const [exportAllResult, setExportAllResult] = useState(null);
  const [exportStep, setExportStep] = useState(null); // "indexando" | "empaquetando"
  const [exportStartedAt, setExportStartedAt] = useState(null);
  const [exportElapsed, setExportElapsed] = useState(0);
  const [exportError, setExportError] = useState(null);
  const exportPollRef = useRef(null);
  const exportTickRef = useRef(null);

  // ── Modo cuenta NUEVA: preflight (paso 1) ──────────────────────────────────
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflight, setPreflight] = useState(null);

  // ── Modo cuenta NUEVA: import guiado (paso 2) ──────────────────────────────
  const [guidedTab, setGuidedTab] = useState("url");
  const [guidedUrl, setGuidedUrl] = useState("");
  const [guidedFile, setGuidedFile] = useState(null);
  const [guidedStage, setGuidedStage] = useState("idle"); // idle|dry_running|dry_done|importing
  const [guidedDryResult, setGuidedDryResult] = useState(null);
  const [guidedConfirmText, setGuidedConfirmText] = useState("");
  const [importJobStatus, setImportJobStatus] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [expectedSha, setExpectedSha] = useState(""); // SHA256 opcional del ZIP
  const importPollRef = useRef(null);

  // ── Modo cuenta NUEVA: verificar (paso 3) ───────────────────────────────────
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyNote, setVerifyNote] = useState(null); // progreso del job que verifica los objetos de Storage
  const verifyCancelRef = useRef(false);

  // ── Modo cuenta NUEVA — "Vaciar esta cuenta antes de importar" (CAMBIO 2) ────
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purgeStage, setPurgeStage] = useState("idle"); // idle|running|done|error
  const [purgeResult, setPurgeResult] = useState(null);
  const [purgeError, setPurgeError] = useState(null);
  const purgePollRef = useRef(null);
  // Guard "cuenta no vacía" (CAMBIO 3) + forzado explícito
  const [notEmptyBlock, setNotEmptyBlock] = useState(null);
  const [forceImport, setForceImport] = useState(false);

  // ── Herramientas de reparación (acordeón, al final) ────────────────────────
  const [toolsOpen, setToolsOpen] = useState(false);
  const [cleanupColls, setCleanupColls] = useState({ branches: true, ops_kpi_definitions: true });
  const [cleanupForce, setCleanupForce] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupDidDry, setCleanupDidDry] = useState(false);
  const [remapBulk, setRemapBulk] = useState(false);
  const [remapCollection, setRemapCollection] = useState("purchase_orders");
  const [remapField, setRemapField] = useState("branch_id");
  const [remapMapping, setRemapMapping] = useState("");
  const [remapLoading, setRemapLoading] = useState(false);
  const [remapResult, setRemapResult] = useState(null);
  const [remapDidDry, setRemapDidDry] = useState(false);

  const hdr = () => ({ headers: { "X-Migration-Token": token } });

  // El detail de un 401 de token de migración es un objeto {code, message}; el
  // resto de los errores traen detail string. Este helper devuelve SIEMPRE un
  // texto seguro para toasts (nunca [object Object]).
  const detailMsg = (e, fallback = "Error") => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && d.message) return d.message;
    return fallback;
  };
  const isMigrationTokenError = (e) =>
    e?.response?.status === 401 && e?.response?.data?.detail?.code === "migration_token_invalid";
  // Centraliza el manejo de errores de las acciones protegidas por token: si es
  // un token de migración inválido, lo muestra INLINE junto al input (sin
  // desloguear ni borrar lo tipeado). El resto va al toast normal.
  const handleActionError = (e, fallback = "Error") => {
    if (isMigrationTokenError(e)) {
      setTokenError(detailMsg(e, "X-Migration-Token inválido"));
      toast.error("El token de migración no es válido — revísalo arriba");
    } else {
      toast.error(detailMsg(e, fallback));
    }
  };

  const loadInfo = async () => {
    try {
      const r = await api.get("/admin/migration/info");
      setInfo(r.data);
      setBackfill(r.data.backfill_job);
    } catch (e) { /* noop */ }
  };
  const loadJobs = async () => {
    try { const r = await api.get("/admin/migration/jobs"); setJobs(r.data.jobs || []); }
    catch (e) { /* noop */ }
  };
  const loadMode = async () => {
    try { const r = await api.get("/admin/migration/mode"); setMigrationMode(!!r.data.migration_mode); }
    catch (e) { /* noop */ }
  };

  const startImportPolling = () => {
    clearInterval(importPollRef.current);
    importPollRef.current = setInterval(async () => {
      try {
        // Manda X-Migration-Token: si el import ya reemplazó "users", el JWT de
        // quien lo disparó quedó inválido, pero el polling sigue funcionando.
        const r = await api.get("/admin/migration/import/status", hdr());
        setImportJobStatus(r.data);
        if (r.data.status === "done") {
          clearInterval(importPollRef.current);
          setImportResult(r.data.result || null);
          if (r.data.result?.dry_run) {
            setGuidedDryResult(r.data.result);
            setGuidedStage("dry_done");
            toast.success("Análisis listo — revisa lo que va a entrar");
          } else {
            setGuidedStage("done");
            toast.success("Import completo. Vuelve a entrar con tu email y contraseña de la cuenta vieja.");
          }
        } else if (r.data.status === "error") {
          clearInterval(importPollRef.current);
          setImportResult(r.data.result || null);
          setGuidedStage("error");
          toast.error(r.data.error || "Error en el import");
        }
      } catch (e) { /* noop: puede fallar transitoriamente justo cuando se reemplazan los users */ }
    }, 3000);
  };

  const loadImportStatus = async () => {
    try {
      const r = await api.get("/admin/migration/import/status");
      if (r.data && r.data.status && r.data.status !== "never_run") {
        setImportJobStatus(r.data);
        if (r.data.status === "done" || r.data.status === "error") {
          setImportResult(r.data.result || null);
          if (r.data.status === "done" && r.data.result?.dry_run) setGuidedDryResult(r.data.result);
        }
        if (r.data.status === "running") startImportPolling();
      }
    } catch (e) { /* noop */ }
  };

  // ── Modo cuenta VIEJA: export en background — sobrevive a un reload de la
  // pantalla a mitad de camino, retomando el polling desde el estado del server.
  const startExportTicker = (startedAtIso) => {
    clearInterval(exportTickRef.current);
    const startedMs = startedAtIso ? new Date(startedAtIso).getTime() : Date.now();
    exportTickRef.current = setInterval(() => setExportElapsed(Math.floor((Date.now() - startedMs) / 1000)), 1000);
  };
  const startExportPolling = () => {
    clearInterval(exportPollRef.current);
    exportPollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/admin/migration/export/status", hdr());
        if (r.data.status === "running") {
          setExportStep(r.data.step || null);
        } else if (r.data.status === "done") {
          clearInterval(exportPollRef.current);
          clearInterval(exportTickRef.current);
          setExportingAll(false);
          setExportAllResult(r.data.result || null);
          toast.success("Export listo");
          loadJobs();
        } else if (r.data.status === "error") {
          clearInterval(exportPollRef.current);
          clearInterval(exportTickRef.current);
          setExportingAll(false);
          setExportError(r.data.error || "Error al exportar");
          toast.error(r.data.error || "Error al exportar");
        }
      } catch (e) { /* noop: red transitoria, sigue reintentando */ }
    }, 5000);
  };
  const loadExportStatus = async () => {
    try {
      const r = await api.get("/admin/migration/export/status");
      if (!r.data || !r.data.status || r.data.status === "never_run") return;
      if (r.data.status === "running") {
        setExportingAll(true);
        setExportStep(r.data.step || null);
        setExportStartedAt(r.data.started_at || null);
        startExportTicker(r.data.started_at);
        startExportPolling();
      } else if (r.data.status === "done") {
        setExportAllResult(r.data.result || null);
      } else if (r.data.status === "error") {
        setExportError(r.data.error || null);
      }
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    loadInfo(); loadJobs(); loadImportStatus(); loadExportStatus(); loadMode();
    verifyCancelRef.current = false;
    return () => {
      verifyCancelRef.current = true;
      clearInterval(pollRef.current); clearInterval(importPollRef.current);
      clearInterval(exportPollRef.current); clearInterval(exportTickRef.current);
      clearInterval(purgePollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveToken = async () => {
    if ((newToken || "").length < 32) { toast.error("El token debe tener al menos 32 caracteres"); return; }
    const wasConfigured = !!info?.token_configured;
    setSavingToken(true);
    try {
      await api.post("/admin/migration/setup-token", { token: newToken });
      toast.success(wasConfigured ? "Token nuevo configurado" : "Token configurado");
      setToken(newToken);
      setNewToken("");
      setShowResetToken(false);
      await loadInfo();
    } catch (e) { toast.error(detailMsg(e)); }
    finally { setSavingToken(false); }
  };

  // 48 caracteres aleatorios (A-Z a-z 0-9) via crypto.getRandomValues — nunca se
  // loguea ni se manda a otro endpoint que no sea setup-token.
  const generateSecureToken = () => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const arr = new Uint32Array(48);
    window.crypto.getRandomValues(arr);
    const tok = Array.from(arr, (n) => alphabet[n % alphabet.length]).join("");
    setNewToken(tok);
    setNewTokenVisible(true);
  };

  const copyText = (text) => {
    try { navigator.clipboard.writeText(text); toast.success("Copiado"); }
    catch { toast.error("No se pudo copiar; copia el texto manualmente"); }
  };

  // ── Modo cuenta VIEJA ───────────────────────────────────────────────────────
  const runExportAll = async () => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    setExportingAll(true);
    setExportAllResult(null);
    setExportError(null);
    setExportStep("indexando");
    try {
      const r = await api.post("/admin/migration/export", { include_storage: true, run_backfill: true }, hdr());
      setExportStartedAt(new Date().toISOString());
      startExportTicker(new Date().toISOString());
      startExportPolling();
      toast.message("Exportando en background — esto puede tardar unos minutos.");
    } catch (e) {
      handleActionError(e, "Error al exportar");
      setExportingAll(false);
    }
  };

  const deleteJob = async (jid) => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    if (!window.confirm("Borrar este ZIP del servidor?")) return;
    try {
      await api.delete(`/admin/migration/jobs/${jid}`, hdr());
      toast.success("Job borrado");
      if (exportAllResult?.job_id === jid) setExportAllResult(null);
      await loadJobs();
    } catch (e) { handleActionError(e); }
  };

  // ── Modo cuenta NUEVA — paso 1: preflight ──────────────────────────────────
  const runPreflight = async () => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    setPreflightLoading(true);
    try {
      const r = await api.get("/admin/migration/preflight", hdr());
      setPreflight(r.data);
      if ((r.data.blockers || []).length === 0) toast.success("La cuenta está lista para importar");
      else toast.error("Hay cosas que resolver antes de importar");
    } catch (e) { handleActionError(e, "Error al revisar la cuenta"); }
    finally { setPreflightLoading(false); }
  };
  const preflightOk = !!preflight && (preflight.blockers || []).length === 0;

  // ── Modo cuenta NUEVA — paso 2: import guiado (dry-run → confirmar → real) ─
  const guidedSourceReady = guidedTab === "url" ? !!guidedUrl.trim() : !!guidedFile;
  const expectedShaTrim = expectedSha.trim().toLowerCase();
  const expectedShaInvalid = !!expectedShaTrim && !/^[0-9a-f]{64}$/.test(expectedShaTrim);
  const shaExtra = expectedShaTrim ? { expected_sha256: expectedShaTrim } : {};
  const dryBlocked = guidedDryResult?.ok === false || guidedDryResult?.validation?.ok === false;

  const runGuidedDryRun = async () => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    if (!guidedSourceReady) { toast.error(guidedTab === "url" ? "Pega la URL del export" : "Elige un archivo ZIP"); return; }
    if (expectedShaInvalid) { toast.error("El SHA256 esperado debe tener 64 caracteres hexadecimales"); return; }
    setGuidedStage("dry_running");
    setGuidedDryResult(null);
    setNotEmptyBlock(null);
    try {
      if (guidedTab === "url") {
        await api.post("/admin/migration/import", { source_url: guidedUrl.trim(), dry_run: true, overwrite: true, force: forceImport, ...shaExtra }, hdr());
      } else {
        const fd = new FormData();
        fd.append("file", guidedFile);
        fd.append("flags", JSON.stringify({ dry_run: true, overwrite: true, force: forceImport, ...shaExtra }));
        await api.post("/admin/migration/import", fd, { headers: { "X-Migration-Token": token, "Content-Type": "multipart/form-data" } });
      }
      toast.message("Analizando el ZIP...");
      startImportPolling();
    } catch (e) { handleImportError(e, "idle"); }
  };

  const runGuidedRealImport = async () => {
    if (guidedConfirmText.trim().toUpperCase() !== "REEMPLAZAR") { toast.error('Escribe "REEMPLAZAR" para confirmar'); return; }
    if (dryBlocked) { toast.error("El análisis previo encontró errores: corrígelos antes de reemplazar"); return; }
    if (expectedShaInvalid) { toast.error("El SHA256 esperado debe tener 64 caracteres hexadecimales"); return; }
    setGuidedStage("importing");
    setImportResult(null);
    try {
      if (guidedTab === "url") {
        await api.post("/admin/migration/import", { source_url: guidedUrl.trim(), dry_run: false, overwrite: true, force: forceImport, ...shaExtra }, hdr());
      } else {
        const fd = new FormData();
        fd.append("file", guidedFile);
        fd.append("flags", JSON.stringify({ dry_run: false, overwrite: true, force: forceImport, ...shaExtra }));
        await api.post("/admin/migration/import", fd, { headers: { "X-Migration-Token": token, "Content-Type": "multipart/form-data" } });
      }
      toast.message("Importando en background — puede tardar varios minutos.");
      startImportPolling();
    } catch (e) { handleImportError(e, "dry_done"); }
  };

  // "Paso 2 pasó en verde" tiene que sobrevivir a un logout/login (el import
  // real desloguea al admin) → se lee del estado persistido en el servidor,
  // NO de guidedStage (que se pierde si el navegador recarga o redirige).
  // Un import con errores queda en status "error" en el servidor (nunca "done"); el
  // chequeo de result.ok es una segunda barrera para que el paso 3 no se habilite
  // sobre un import que no terminó limpio.
  // Reconciliación de Storage post-import: la acredita el backend (acreditacion_paso2, derivada y de solo lectura);
  // el import sigue en "error" histórico y no es un import limpio.
  const acred2 = importJobStatus?.acreditacion_paso2;
  const step2Recon = importJobStatus?.status === "error" && importJobStatus?.dry_run === false
    && acred2?.ok === true && acred2?.modo === "storage_post_import" && acred2?.clean_import === false;
  const step2Done = (importJobStatus?.status === "done" && importJobStatus?.dry_run === false
    && importJobStatus?.result?.ok !== false) || step2Recon;
  const importRunning = guidedStage === "dry_running" || guidedStage === "importing" || importJobStatus?.status === "running";

  // ── Modo cuenta NUEVA — paso 3: verificar ──────────────────────────────────
  const runVerify = async () => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    setVerifyLoading(true);
    setVerifyNote(null);
    try {
      let r = await api.get("/admin/migration/verify", hdr());
      setVerifyResult(r.data);
      // Los objetos de Storage NO se prueban con el conteo de storage_index: se lanza
      // el job que los lee uno por uno (existencia, tamaño y SHA256) y se espera.
      if (r.data.deep_required && r.data.job?.ok) {
        setVerifyNote("Verificando los objetos de Storage uno por uno (existencia, tamaño y SHA256)… puede tardar varios minutos.");
        let jid = null;
        try {
          const st = await api.post("/admin/migration/verify-storage", {}, hdr());
          jid = st.data?.job_id || null;
        } catch (e) {
          const d = e?.response?.data?.detail;
          if (!(e?.response?.status === 409 && d && d.code === "verify_running")) throw e;
        }
        let finished = false;
        for (let i = 0; i < 400 && !finished && !verifyCancelRef.current; i++) {
          await new Promise((res) => setTimeout(res, 3000));
          const s = await api.get("/admin/migration/verify-storage/status", hdr());
          const sd = s.data || {};
          if (jid && sd.job_id && sd.job_id !== jid) continue; // todavía figura el job anterior
          finished = sd.status === "done" || sd.status === "error";
          if (sd.status === "error") toast.error(sd.error || "Falló la verificación de objetos");
        }
        if (verifyCancelRef.current) return;
        r = await api.get("/admin/migration/verify", hdr());
        setVerifyResult(r.data);
      }
      if (r.data.ok) toast.success("Verificado: documentos y objetos de Storage");
      else if ((r.data.pending || []).length > 0 && (r.data.errors || []).length === 0) toast.message("Verificación incompleta — mira los pendientes");
      else toast.error("La verificación encontró diferencias — revisa el detalle");
    } catch (e) {
      handleActionError(e, "Error al verificar (¿volviste a entrar con el email y contraseña de la cuenta vieja?)");
    } finally { setVerifyLoading(false); setVerifyNote(null); }
  };

  // ── Modo cuenta NUEVA — "Vaciar esta cuenta antes de importar" (CAMBIO 2) ────
  const startPurgePolling = () => {
    clearInterval(purgePollRef.current);
    purgePollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/admin/migration/purge/status", hdr());
        if (r.data.status === "done") {
          clearInterval(purgePollRef.current);
          setPurgeStage("done");
          setPurgeResult(r.data.result || null);
          setNotEmptyBlock(null);
          toast.success("Cuenta vaciada — lista para importar");
          runPreflight();
        } else if (r.data.status === "error") {
          clearInterval(purgePollRef.current);
          setPurgeStage("error");
          setPurgeError(r.data.error || "Error al vaciar");
          toast.error(r.data.error || "Error al vaciar");
        }
      } catch (e) { /* noop: transitorio */ }
    }, 1500);
  };

  const runPurge = async () => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    if (purgeConfirmText.trim().toUpperCase() !== "VACIAR") { toast.error('Escribe "VACIAR" para confirmar'); return; }
    setPurgeStage("running"); setPurgeResult(null); setPurgeError(null);
    try {
      await api.post("/admin/migration/purge", { confirm: purgeConfirmText.trim() }, hdr());
      toast.message("Vaciando la cuenta en background...");
      startPurgePolling();
    } catch (e) {
      setPurgeStage("idle");
      handleActionError(e, "No se pudo vaciar la cuenta");
    }
  };

  // Manejo del guard 409 "account_not_empty" (CAMBIO 3): si la cuenta tiene data,
  // el backend rechaza el import y devolvemos el detalle para guiar a "Vaciar".
  const handleImportError = (e, fallbackStage) => {
    const detail = e?.response?.data?.detail;
    if (isMigrationTokenError(e)) {
      setTokenError(detailMsg(e, "X-Migration-Token inválido"));
      toast.error("El token de migración no es válido — revísalo arriba");
    } else if (e?.response?.status === 409 && detail && detail.code === "account_not_empty") {
      setNotEmptyBlock(detail);
      toast.error("La cuenta no está vacía — usa 'Vaciar' primero");
    } else {
      toast.error(detailMsg(e, "Error"));
    }
    setGuidedStage(fallbackStage);
  };

  // ── Herramientas de reparación ──────────────────────────────────────────────
  const startBackfillPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/admin/migration/backfill-storage/status");
        setBackfill(r.data);
        if (r.data.status === "done" || r.data.status === "error") {
          clearInterval(pollRef.current);
          loadInfo();
        }
      } catch (e) { /* noop */ }
    }, 5000);
  };
  const runBackfill = async (force) => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    try {
      await api.post("/admin/migration/backfill-storage", { force_rebuild: !!force }, hdr());
      toast.success("Backfill iniciado");
      setBackfill({ status: "running", scanned_documents: 0, paths_found: 0, indexed: 0 });
      startBackfillPolling();
    } catch (e) { handleActionError(e); }
  };

  const runCleanup = async (dry) => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    const collections = Object.keys(cleanupColls).filter((k) => cleanupColls[k]);
    if (collections.length === 0) { toast.error("Elige al menos una colección"); return; }
    if (!dry && !window.confirm("Esto BORRA documentos duplicados de forma permanente. ¿Continuar?")) return;
    setCleanupLoading(true);
    if (dry) setCleanupResult(null);
    try {
      const r = await api.post("/admin/migration/cleanup-duplicates",
        { collections, dry_run: dry, force_delete_unreferenced: cleanupForce }, hdr());
      setCleanupResult(r.data);
      setCleanupDidDry(!!dry);
      toast.success(dry ? "Análisis (dry-run) completo" : "Cleanup ejecutado");
    } catch (e) { handleActionError(e, "Error en cleanup"); }
    finally { setCleanupLoading(false); }
  };

  const runRemap = async (dry) => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    let mapping;
    try { mapping = JSON.parse(remapMapping || "{}"); }
    catch { toast.error("El mapping no es JSON válido"); return; }
    if (!mapping || typeof mapping !== "object" || Array.isArray(mapping) || Object.keys(mapping).length === 0) {
      toast.error("El mapping debe ser un objeto JSON {viejo: nuevo} no vacío"); return;
    }
    if (!remapField.trim()) { toast.error("Indica el campo a remappear"); return; }
    if (!remapBulk && !remapCollection.trim()) { toast.error("Indica la colección"); return; }
    if (!dry && !window.confirm("Esto ACTUALIZA documentos de forma permanente. ¿Continuar?")) return;
    setRemapLoading(true);
    if (dry) setRemapResult(null);
    try {
      let r;
      if (remapBulk) {
        r = await api.post("/admin/migration/remap-refs-bulk",
          { field: remapField.trim(), mapping, collections: "*", dry_run: dry }, hdr());
      } else {
        r = await api.post("/admin/migration/remap-refs",
          { collection: remapCollection.trim(), field: remapField.trim(), mapping, dry_run: dry }, hdr());
      }
      setRemapResult(r.data);
      setRemapDidDry(!!dry);
      toast.success(dry ? "Análisis (dry-run) completo" : "Remap ejecutado");
    } catch (e) { handleActionError(e, "Error en remap"); }
    finally { setRemapLoading(false); }
  };

  const Section = ({ icon: Icon, title, children, testid }) => (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-200 bg-slate-50">
        <Icon className="w-4 h-4 text-slate-600" />
        <h3 className="font-display font-bold text-base">{title}</h3>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );

  const StepBadge = ({ n, done }) => (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${done ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>
      {done ? <CheckCircle2 className="w-4 h-4" /> : n}
    </span>
  );

  return (
    <>
      <Topbar title="Migración de cuenta" subtitle="Cambiar todo el sistema de una cuenta a otra" />
      <div className="p-6 pb-28 space-y-5 max-w-4xl">
        {migrationMode !== null && (
          migrationMode ? (
            <div data-testid="migration-mode-banner" data-mode="active"
              className="flex items-start gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
              <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
              <span>Modo migración activo — no se envían correos ni notificaciones, y los procesos automáticos están apagados.</span>
            </div>
          ) : (
            <div data-testid="migration-mode-banner" data-mode="off"
              className="flex items-start gap-3 rounded-xl border border-slate-300 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-600">
              <Info className="w-5 h-5 shrink-0 mt-0.5" />
              <span>Modo migración apagado — la cuenta opera normal.</span>
            </div>
          )
        )}
        <div data-testid="migration-warning-banner" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <span>Esta funcionalidad es solo para migración de cuenta. No usar en operación normal.</span>
        </div>

        {/* Toggle de modo */}
        <div data-testid="migration-mode-toggle" className="flex gap-2 bg-slate-100 rounded-xl p-1.5">
          <button data-testid="migration-mode-old-btn" onClick={() => setMode("old")}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold transition ${mode === "old" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}>
            Soy la cuenta VIEJA (exportar)
          </button>
          <button data-testid="migration-mode-new-btn" onClick={() => setMode("new")}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold transition ${mode === "new" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}>
            Soy la cuenta NUEVA (importar)
          </button>
        </div>

        {/* Token */}
        <Section icon={KeyRound} title="Token de migración" testid="migration-token-section">
          <p className="text-sm text-slate-600">
            Estado: {info?.token_configured
              ? <span className="font-semibold text-emerald-700">Configurado</span>
              : <span className="font-semibold text-red-700">No configurado</span>}
          </p>

          {info?.token_configured && !showResetToken && (
            <button data-testid="migration-open-reset-token-btn" onClick={() => setShowResetToken(true)}
              className="text-sm font-semibold text-slate-600 underline hover:text-slate-900">
              No recuerdo el token — poner uno nuevo
            </button>
          )}

          {info && (!info.token_configured || showResetToken) && (
            <div data-testid="migration-setup-token-block" className="space-y-3">
              {info.token_configured && (
                <div data-testid="migration-reset-token-warning" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span>El token anterior deja de funcionar. Si estás a mitad de una migración, vas a tener que usar el nuevo token en las dos cuentas.</span>
                </div>
              )}

              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[260px]">
                  <label className="text-xs font-semibold text-slate-500">Nuevo token (mín. 32 caracteres)</label>
                  <div className="mt-1 flex gap-2">
                    <div className="relative flex-1">
                      <input data-testid="migration-newtoken-input" value={newToken} onChange={(e) => setNewToken(e.target.value)}
                        type={newTokenVisible ? "text" : "password"}
                        className="w-full px-3 py-2 pr-9 rounded-lg border border-slate-300 text-sm font-mono" placeholder="token-largo-y-aleatorio-..." />
                      <button type="button" data-testid="migration-toggle-newtoken-visibility"
                        onClick={() => setNewTokenVisible((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                        {newTokenVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button type="button" data-testid="migration-generate-token-btn" onClick={generateSecureToken}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-300 hover:bg-slate-50 whitespace-nowrap">
                      <Sparkles className="w-4 h-4" /> Generar token seguro
                    </button>
                    <button type="button" data-testid="migration-copy-newtoken-btn" onClick={() => copyText(newToken)} disabled={!newToken}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                      <Copy className="w-4 h-4" /> Copiar
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Cópialo y guárdalo ahora en un lugar seguro. No se puede volver a ver.</p>
                </div>
                <button data-testid="migration-savetoken-btn" onClick={saveToken} disabled={savingToken}
                  className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm whitespace-nowrap">
                  {savingToken ? "Guardando..." : "Configurar token"}
                </button>
              </div>

              {info.token_configured && (
                <button data-testid="migration-cancel-reset-token-btn" onClick={() => { setShowResetToken(false); setNewToken(""); }}
                  className="text-xs font-semibold text-slate-400 underline hover:text-slate-600">
                  Cancelar
                </button>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-500">X-Migration-Token (para esta sesión)</label>
            <input data-testid="migration-token-input" value={token} onChange={(e) => { setToken(e.target.value); setTokenError(null); }} type="password"
              className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm font-mono ${tokenError ? "border-red-400 bg-red-50" : "border-slate-300"}`} placeholder="Pega el token para habilitar las acciones" />
            {tokenError && (
              <p data-testid="migration-token-error" className="text-xs font-semibold text-red-600 mt-1 flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5 shrink-0" /> {tokenError}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-1">No se guarda; se usa solo en esta pantalla para firmar las llamadas.</p>
          </div>
        </Section>

        {/* ═══════════════ MODO CUENTA VIEJA ═══════════════ */}
        {mode === "old" && (
          <Section icon={DownloadCloud} title="Preparar y exportar todo" testid="migration-export-section">
            <p className="text-sm text-slate-600">
              Un solo paso: actualiza el índice de archivos, empaqueta todas las colecciones y todos
              los archivos de Storage en un ZIP, y te da un link listo para pegar en la cuenta nueva.
            </p>
            <button data-testid="migration-export-all-btn" onClick={runExportAll} disabled={exportingAll}
              className="btn-primary-chizy px-4 py-3 rounded-xl text-sm inline-flex items-center gap-2 font-bold">
              {exportingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
              {exportingAll ? "Preparar y exportar todo" : "Preparar y exportar todo"}
            </button>

            {exportingAll && (
              <div data-testid="migration-export-progress" className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                <span>
                  {exportStep === "indexando" ? "Actualizando índice de archivos..." : "Empaquetando..."}
                  {" "}({exportElapsed}s) — corre en background, puedes dejar la pestaña abierta.
                </span>
              </div>
            )}

            {exportError && !exportingAll && (
              <div data-testid="migration-export-error" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center gap-2">
                <XCircle className="w-4 h-4 shrink-0" /> {exportError}
              </div>
            )}

            {exportAllResult && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm space-y-3" data-testid="migration-export-result">
                <div className="flex items-center gap-2 font-semibold text-emerald-800"><CheckCircle2 className="w-4 h-4" /> Export listo</div>
                <div>
                  Colecciones: <b>{Object.keys(exportAllResult.counts?.collections || {}).length}</b>
                  {" · "}Storage: <b>{exportAllResult.counts?.storage_objects}</b> objetos
                  {" · "}Tamaño: <b>{fmtBytes(exportAllResult.size_bytes)}</b>
                </div>
                <div className="font-mono text-xs text-slate-600 break-all">checksum: {exportAllResult.checksum_sha256}</div>
                {exportAllResult.backfill && (
                  <div className="text-xs text-slate-600">
                    Índice actualizado antes de exportar: indexados {exportAllResult.backfill.indexed}, ya estaban {exportAllResult.backfill.already_present}, preservados {exportAllResult.backfill.preserved_from_previous_index}, fallidos {exportAllResult.backfill.failed}
                  </div>
                )}
                {exportAllResult.excluded_collections?.length > 0 && (
                  <div className="text-xs text-slate-500">Excluidas del export: {exportAllResult.excluded_collections.map((e) => e.name).join(", ")}</div>
                )}
                {exportAllResult.storage_failed_paths?.length > 0 && (
                  <div data-testid="migration-export-storagefailed-warning" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{exportAllResult.storage_failed_paths.length} archivo(s) del índice ya no existen en Storage y no se pudieron empaquetar.</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <button data-testid="migration-copyurl-btn" onClick={() => copyText(exportAllResult.download_url_absolute || (ORIGIN + exportAllResult.download_url))}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-white">
                    <Copy className="w-3.5 h-3.5" /> Copiar URL para la cuenta nueva
                  </button>
                  <a data-testid="migration-download-link" href={ORIGIN + exportAllResult.download_url} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-white">
                    <HardDriveDownload className="w-3.5 h-3.5" /> Descargar al disco
                  </a>
                  <button data-testid="migration-delete-current-btn" onClick={() => deleteJob(exportAllResult.job_id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" /> Borrar el archivo del servidor
                  </button>
                </div>
                <div className="text-xs text-slate-500">Usa "Borrar el archivo del servidor" solo cuando ya terminaste de importarlo en la cuenta nueva — después de borrarlo, la URL de descarga deja de funcionar.</div>
              </div>
            )}

            <div data-testid="migration-secret-warning" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>Este ZIP contiene las contraseñas iniciales de todos los usuarios. Trátalo como un archivo secreto y bórralo cuando termines la migración.</span>
            </div>
            <div data-testid="migration-noredeploy-warning" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>El archivo queda guardado en Storage (sobrevive a un redeploy) y se limpia solo a las 24hs — igual, bórralo manualmente en cuanto termines de importar en la cuenta nueva.</span>
            </div>

            {jobs.length > 0 && (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr><th className="text-left px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Job</th><th className="text-left px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Tamaño</th><th className="text-left px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Creado</th><th className="px-4 py-2"></th></tr>
                  </thead>
                  <tbody>
                    {jobs.map((j) => (
                      <tr key={j.job_id} className={`border-b border-slate-100 last:border-0 ${!j.file_exists ? "bg-red-50" : ""}`} data-testid={`migration-job-row-${j.job_id}`}>
                        <td className="px-4 py-2 font-mono text-xs">
                          {j.job_id.slice(0, 10)}…
                          {!j.file_exists && <div className="text-red-600 font-semibold mt-0.5">El archivo ya no está en el servidor — vuelve a exportar</div>}
                        </td>
                        <td className="px-4 py-2">{fmtBytes(j.size_bytes)}</td>
                        <td className="px-4 py-2 text-slate-500">{new Date(j.created_at).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => deleteJob(j.job_id)} data-testid={`migration-deljob-${j.job_id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" /> Limpiar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        {/* ═══════════════ MODO CUENTA NUEVA ═══════════════ */}
        {mode === "new" && (
          <>
            {/* Paso 1 */}
            <Section icon={ListChecks} title={<span className="inline-flex items-center gap-2"><StepBadge n={1} done={preflightOk} /> Revisar la cuenta</span>} testid="migration-preflight-section">
              <button data-testid="migration-preflight-btn" onClick={runPreflight} disabled={preflightLoading}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 hover:bg-slate-50">
                {preflightLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Revisar la cuenta
              </button>

              {preflight && (
                <div data-testid="migration-preflight-result" className="space-y-3">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div><span className="text-slate-500">Usuarios:</span> <b>{preflight.db?.users_count}</b></div>
                    <div><span className="text-slate-500">Colecciones:</span> <b>{preflight.db?.collections_count}</b></div>
                    <div><span className="text-slate-500">¿Cuenta nueva?:</span> <b>{preflight.db?.looks_fresh ? "sí" : "no"}</b></div>
                    <div><span className="text-slate-500">ALLOW_RESEED:</span> <b className={preflight.allow_reseed ? "text-red-600" : "text-emerald-700"}>{String(preflight.allow_reseed)}</b></div>
                    <div><span className="text-slate-500">Storage:</span> <b className={preflight.storage_ok ? "text-emerald-700" : "text-red-600"}>{preflight.storage_ok ? "OK" : "falló"}</b></div>
                  </div>

                  {preflight.blockers?.length > 0 ? (
                    <div data-testid="migration-preflight-blockers" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-1">
                      <div className="font-semibold flex items-center gap-1.5"><XCircle className="w-4 h-4" /> Hay que resolver esto antes de importar:</div>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {preflight.blockers.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 font-semibold flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" /> La cuenta está lista para importar
                    </div>
                  )}

                  <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer font-semibold select-none">Ver variables de entorno chequeadas (sin valores)</summary>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 font-mono">
                      {Object.entries(preflight.env_vars || {}).map(([k, v]) => (
                        <div key={k} className={v.set ? "text-emerald-700" : "text-red-600"}>{v.set ? "✓" : "✗"} {k}</div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </Section>

            {/* Vaciar esta cuenta — SOLO con MIGRATION_MODE=true (CAMBIO 2) */}
            {migrationMode === true && (
              <Section icon={Trash2} title="Vaciar esta cuenta antes de importar" testid="migration-purge-section">
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-2">
                  <p>
                    Borra <b>todas las colecciones</b> de esta cuenta, salvo el <b>usuario administrador inicial</b> y la
                    <b> configuración de migración</b> (el token y su secreto). Deja la cuenta realmente vacía antes de
                    importar para que la data de ejemplo no choque con la real.
                  </p>
                  <p className="text-xs">Solo disponible en modo migración. Corre en background; el avance se ve abajo.</p>
                </div>

                {purgeStage !== "running" && purgeStage !== "done" && (
                  <div className="flex gap-2 items-end mt-3">
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-slate-500">Escribe VACIAR para confirmar</label>
                      <input data-testid="migration-purge-confirm-input" value={purgeConfirmText} onChange={(e) => setPurgeConfirmText(e.target.value)}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-red-300 text-sm font-mono" placeholder="VACIAR" />
                    </div>
                    <button data-testid="migration-purge-btn" onClick={runPurge}
                      disabled={purgeConfirmText.trim().toUpperCase() !== "VACIAR"}
                      className="px-4 py-2.5 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
                      <Trash2 className="w-4 h-4" /> Vaciar esta cuenta
                    </button>
                  </div>
                )}

                {purgeStage === "running" && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center gap-2 mt-3" data-testid="migration-purge-running">
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" /> Vaciando la cuenta en background...
                  </div>
                )}

                {purgeStage === "error" && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 mt-3" data-testid="migration-purge-error">
                    Error al vaciar: {purgeError}
                  </div>
                )}

                {purgeStage === "done" && purgeResult && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm space-y-2 mt-3" data-testid="migration-purge-result">
                    <div className="font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Cuenta vaciada</div>
                    <div>Documentos borrados: <b>{purgeResult.total_deleted}</b> en <b>{purgeResult.collections_affected}</b> colecciones · Usuarios que quedan: <b>{purgeResult.users_remaining}</b></div>
                    {Object.keys(purgeResult.deleted || {}).length > 0 && (
                      <details className="text-xs text-slate-600">
                        <summary className="cursor-pointer font-semibold select-none">Ver qué se borró</summary>
                        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1 font-mono">
                          {Object.entries(purgeResult.deleted).map(([k, v]) => (
                            <div key={k}>{k}: <b>{v}</b></div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </Section>
            )}

            {/* Paso 2 */}
            <Section icon={UploadCloud} title={<span className="inline-flex items-center gap-2"><StepBadge n={2} done={step2Done} /> Importar todo</span>} testid="migration-import-section">
              <div className={!preflightOk ? "opacity-40 pointer-events-none" : ""} data-testid="migration-import-step2-body">
                {!preflightOk && (
                  <p className="text-xs text-red-600 font-semibold mb-3">Primero completa el paso 1 sin bloqueos.</p>
                )}
                {notEmptyBlock && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-2 mb-3" data-testid="migration-import-blocked">
                    <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> La cuenta no está vacía</div>
                    <p>{notEmptyBlock.message}</p>
                    {notEmptyBlock.details && Object.keys(notEmptyBlock.details).length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 font-mono text-xs">
                        {Object.entries(notEmptyBlock.details).map(([k, v]) => (
                          <div key={k}>{k}: <b>{v}</b></div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs">Vacía la cuenta arriba (recomendado). Si aún así quieres importar encima, confirma el forzado:</p>
                    <label className="flex items-center gap-2 text-xs font-semibold">
                      <input type="checkbox" data-testid="migration-force-checkbox" checked={forceImport} onChange={(e) => setForceImport(e.target.checked)} />
                      Entiendo el riesgo: importar aunque la cuenta tenga data (forzar)
                    </label>
                  </div>
                )}
                <div className="flex gap-2 mb-3">
                  <button onClick={() => setGuidedTab("url")} data-testid="migration-tab-url" className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${guidedTab === "url" ? "bg-slate-900 text-white" : "border border-slate-300"}`}>Desde URL</button>
                  <button onClick={() => setGuidedTab("file")} data-testid="migration-tab-file" className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${guidedTab === "file" ? "bg-slate-900 text-white" : "border border-slate-300"}`}>Desde archivo</button>
                </div>

                {guidedTab === "url" ? (
                  <div className="mb-3">
                    <label className="text-xs font-semibold text-slate-500">URL del export (cuenta vieja)</label>
                    <input data-testid="migration-sourceurl-input" value={guidedUrl} onChange={(e) => setGuidedUrl(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono" placeholder="https://.../api/admin/migration/download/...?exp=..&sig=.." />
                  </div>
                ) : (
                  <div className="mb-3">
                    <label className="text-xs font-semibold text-slate-500">Archivo ZIP</label>
                    <input data-testid="migration-file-input" type="file" accept=".zip" onChange={(e) => setGuidedFile(e.target.files?.[0] || null)}
                      className="w-full mt-1 text-sm" />
                  </div>
                )}

                <div className="mb-3">
                  <label className="text-xs font-semibold text-slate-500">SHA256 del ZIP (opcional — lo muestra el export; si lo pegas, el import se rechaza si no coincide)</label>
                  <input data-testid="migration-expectedsha-input" value={expectedSha} onChange={(e) => setExpectedSha(e.target.value)} disabled={importRunning}
                    className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm font-mono ${expectedShaInvalid ? "border-red-400" : "border-slate-300"}`} placeholder="64 caracteres hexadecimales" />
                  {expectedShaInvalid && <p className="text-xs text-red-600 mt-1" data-testid="migration-expectedsha-invalid">Tiene que ser un SHA256 de 64 caracteres hexadecimales.</p>}
                </div>

                {guidedStage !== "dry_done" && guidedStage !== "importing" && guidedStage !== "done" && (
                  <button data-testid="migration-import-all-btn" onClick={runGuidedDryRun} disabled={importRunning || !preflightOk}
                    className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm inline-flex items-center gap-2 font-bold disabled:opacity-40 disabled:cursor-not-allowed">
                    {guidedStage === "dry_running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    {guidedStage === "dry_running" ? "Analizando..." : "Importar todo"}
                  </button>
                )}

                {importJobStatus?.status === "error" && !importRunning && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 space-y-2 mt-3" data-testid="migration-import-errors">
                    <div className="font-semibold flex items-center gap-1.5">
                      <XCircle className="w-4 h-4" />
                      {importResult?.aborted_before_write
                        ? "El ZIP no pasó la validación previa — no se escribió nada"
                        : importJobStatus?.dry_run === false
                          ? "El import terminó con errores — no sigas al paso 3"
                          : "El análisis del ZIP falló"}
                    </div>
                    {importJobStatus?.error && <p>{importJobStatus.error}</p>}
                    {(importResult?.validation?.errors || []).slice(0, 10).map((er, i) => (
                      <div key={`v${i}`} className="font-mono text-xs break-all" data-testid="migration-import-error-item">• {er.message}</div>
                    ))}
                    {importResult?.validation?.error_count > 10 && (
                      <div className="text-xs">… y {importResult.validation.error_count - 10} error(es) más.</div>
                    )}
                    {(importResult?.problems || []).slice(0, 10).map((p, i) => (
                      <div key={`p${i}`} className="font-mono text-xs break-all" data-testid="migration-import-error-item">• {p}</div>
                    ))}
                    {(importResult?.storage_errors || []).slice(0, 5).map((se, i) => (
                      <div key={`s${i}`} className="font-mono text-xs break-all">• {se.path}: {se.error}</div>
                    ))}
                    {importJobStatus?.dry_run === false && !importResult?.aborted_before_write && (
                      <p className="text-xs">Puede haber quedado una carga parcial en esta cuenta. Corrige la causa y vuelve a importar (reemplaza todo de nuevo). El paso 3 queda bloqueado hasta que un import termine sin errores.</p>
                    )}
                  </div>
                )}

                {guidedStage === "dry_done" && guidedDryResult && (
                  <div className="space-y-3" data-testid="migration-dryrun-summary">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                      <div className="font-semibold mb-1">Esto va a entrar:</div>
                      <div>Colecciones: <b>{Object.keys(guidedDryResult.would_import?.collections || {}).length}</b> · Storage: <b>{guidedDryResult.would_import?.storage_objects}</b> objetos</div>
                      {guidedDryResult.validation?.summary && (
                        <div className="mt-1 text-emerald-800" data-testid="migration-dryrun-validation">{guidedDryResult.validation.summary}{guidedDryResult.validation.zip_sha256_verified ? " SHA256 del ZIP verificado." : ""}</div>
                      )}
                      {(guidedDryResult.validation?.exceptions || []).slice(0, 8).map((w, i) => (
                        <div key={i} className="mt-1 text-xs text-amber-800" data-testid="migration-dryrun-exception">⚠ Excepción explícita ({w.code}): {w.message}</div>
                      ))}
                    </div>
                    <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-2">
                      <div className="font-semibold">Confirma para reemplazar todo en esta cuenta</div>
                      <p>Escribe <b>REEMPLAZAR</b> para confirmar. Vas a quedar deslogueado cuando termine: tus usuarios se reemplazan por los de la cuenta vieja. Vuelve a entrar con tu email y la contraseña que usabas en la cuenta vieja.</p>
                      <div className="flex gap-2">
                        <input data-testid="migration-confirm-input" value={guidedConfirmText} onChange={(e) => setGuidedConfirmText(e.target.value)}
                          className="flex-1 px-3 py-2 rounded-lg border border-red-300 text-sm font-mono" placeholder="REEMPLAZAR" />
                        <button data-testid="migration-confirm-btn" onClick={runGuidedRealImport} disabled={guidedConfirmText.trim().toUpperCase() !== "REEMPLAZAR" || !preflightOk || dryBlocked}
                          className="px-4 py-2 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                          Reemplazar todo
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {guidedStage === "importing" && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center gap-2" data-testid="migration-import-running">
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    <span>Importando en background (puede tardar varios minutos) — no cierres esta pestaña. Vas a quedar deslogueado cuando termine.</span>
                  </div>
                )}

                {step2Done && importResult && !importResult.dry_run && importResult.ok !== false && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm space-y-1" data-testid="migration-import-result">
                    <div className="font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Import ejecutado sin errores</div>
                    {(() => {
                      const c = importResult.imported?.collections || {};
                      const ins = Object.values(c).reduce((a, v) => a + (v.inserted || 0), 0);
                      const st = importResult.imported?.storage_objects || {};
                      return <div>Insertados: <b>{ins}</b> · Storage subidos: <b>{st.uploaded}</b> (fallidos {st.failed || 0}, SHA256 distinto {st.checksum_mismatch || 0})</div>;
                    })()}
                    {(importResult.warnings || []).slice(0, 5).map((w, i) => (
                      <div key={i} className="text-xs text-amber-800" data-testid="migration-import-warning">⚠ {w}</div>
                    ))}
                    <div className="text-slate-600">Vuelve a entrar con el email y la contraseña que usabas en la cuenta vieja, y seguí con el paso 3: es el que comprueba que los objetos de Storage existan con el tamaño y SHA256 correctos.</div>
                  </div>
                )}
              </div>
            </Section>

            {/* Reparación acotada de objetos de Storage tras un import con errores. Va FUERA de <Section> (que se
                redefine en cada render y remontaría el componente) y del bloque deshabilitable del paso 2. */}
            {importJobStatus?.status === "error" && importJobStatus?.dry_run === false && !importRunning && (
              <MigrationRepairStorage token={token} sourceUrl={guidedUrl} importJobStatus={importJobStatus}
                importResult={importResult} onActionError={handleActionError} onVerifyTerminal={loadImportStatus} />
            )}

            {/* Paso 3 */}
            <Section icon={ClipboardCheck} title={<span className="inline-flex items-center gap-2"><StepBadge n={3} done={!!verifyResult?.ok} /> Verificar</span>} testid="migration-verify-section">
              <div className={!step2Done ? "opacity-40 pointer-events-none" : ""} data-testid="migration-verify-step3-body">
                {!step2Done && <p className="text-xs text-red-600 font-semibold mb-3">Primero termina el paso 2.</p>}
                {step2Recon && (
                  <p className="text-xs text-amber-800 font-semibold mb-3" data-testid="migration-step2-recon-note">
                    Paso 2 acreditado por reconciliación de Storage post-import ({acred2.objetos_verificados} objeto(s) verificados): no es un import limpio.
                  </p>
                )}
                <button data-testid="migration-verify-btn" onClick={runVerify} disabled={verifyLoading || !step2Done}
                  className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm inline-flex items-center gap-2 font-bold">
                  {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                  Verificar
                </button>

                {verifyNote && (
                  <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center gap-2" data-testid="migration-verify-progress">
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" /><span>{verifyNote}</span>
                  </div>
                )}

                {verifyResult && (
                  <div className="mt-4 space-y-3" data-testid="migration-verify-result">
                    <div className={`text-lg font-bold ${verifyResult.ok ? "text-emerald-700" : ((verifyResult.pending || []).length > 0 && (verifyResult.errors || []).length === 0 ? "text-amber-700" : "text-red-700")}`} data-testid="migration-verify-summary">{verifyResult.summary}</div>
                    {(verifyResult.errors || []).length > 0 && (
                      <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-1" data-testid="migration-verify-errors">
                        {verifyResult.errors.map((m, i) => <div key={i}>• {m}</div>)}
                      </div>
                    )}
                    {(verifyResult.pending || []).length > 0 && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1" data-testid="migration-verify-pending">
                        {verifyResult.pending.map((m, i) => <div key={i}>• {m}</div>)}
                      </div>
                    )}
                    {["missing", "mismatch", "unreadable"].map((k) => {
                      const items = verifyResult.deep_lists?.[k] || [];
                      if (items.length === 0) return null;
                      const label = { missing: "Objetos que no existen en Storage", mismatch: "Objetos con tamaño o SHA256 distinto", unreadable: "Objetos que no se pudieron leer" }[k];
                      return (
                        <div key={k} className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800" data-testid={`migration-verify-deep-${k}`}>
                          <div className="font-semibold mb-1">{label} ({items.length}{items.length >= 200 ? "+" : ""})</div>
                          {items.slice(0, 10).map((it, i) => <div key={i} className="font-mono break-all">{it.path}{it.fields ? ` (${it.fields.join(", ")})` : ""}</div>)}
                          {items.length > 10 && <div>… y {items.length - 10} más.</div>}
                        </div>
                      );
                    })}
                    {(verifyResult.warnings || []).length > 0 && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 space-y-1" data-testid="migration-verify-warnings">
                        {verifyResult.warnings.map((m, i) => <div key={i}>⚠ {m}</div>)}
                      </div>
                    )}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Colección</th>
                            <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Esperado</th>
                            <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Real</th>
                            <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Diferencia</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className={verifyResult.storage?.diff < 0 ? "bg-red-50" : ""} data-testid="migration-verify-row-storage">
                            <td className="px-4 py-2 font-mono">Objetos en Storage <span className="font-sans text-[11px] text-slate-500">(existencia + tamaño + SHA256)</span></td>
                            <td className="px-4 py-2 text-right">{verifyResult.storage?.expected}</td>
                            <td className="px-4 py-2 text-right">{verifyResult.storage?.verified_ok != null ? verifyResult.storage.verified_ok : "—"}</td>
                            <td className={`px-4 py-2 text-right font-semibold ${verifyResult.storage?.diff < 0 ? "text-red-600" : "text-emerald-700"}`}>{verifyResult.storage?.diff != null ? verifyResult.storage.diff : "—"}</td>
                          </tr>
                          {(verifyResult.collections || []).map((r) => (
                            <tr key={r.name} className={r.diff < 0 ? "bg-red-50" : ""} data-testid={`migration-verify-row-${r.name}`}>
                              <td className="px-4 py-2 font-mono">{r.name}{r.note && <div className="font-sans text-[11px] text-slate-500">{r.note}</div>}</td>
                              <td className="px-4 py-2 text-right">{r.expected}</td>
                              <td className="px-4 py-2 text-right">{r.actual}</td>
                              <td className={`px-4 py-2 text-right font-semibold ${r.diff < 0 ? "text-red-600" : "text-emerald-700"}`}>{r.diff}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-slate-500" data-testid="migration-verify-index-note">
                      Entradas en storage_index: {verifyResult.storage?.index_docs}. El conteo del índice no prueba que el objeto exista: eso lo prueba la fila de Storage de arriba.
                    </p>
                    {(verifyResult.exceptions || []).length > 0 && (
                      <details className="text-xs text-slate-600" data-testid="migration-verify-exceptions">
                        <summary className="cursor-pointer font-semibold">Excepciones declaradas ({verifyResult.exceptions.length})</summary>
                        <ul className="mt-1 list-disc pl-5 space-y-0.5">
                          {verifyResult.exceptions.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </Section>
          </>
        )}

        {/* ═══════════════ Herramientas de reparación (acordeón) ═══════════════ */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="migration-tools-accordion">
          <button onClick={() => setToolsOpen((o) => !o)} data-testid="migration-tools-toggle-btn"
            className="w-full flex items-center justify-between gap-2 px-6 py-4 bg-slate-50 hover:bg-slate-100 transition">
            <span className="flex items-center gap-2 font-display font-bold text-base"><Wrench className="w-4 h-4 text-slate-600" /> Herramientas de reparación (solo si la verificación da error)</span>
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
          </button>

          {toolsOpen && (
            <div className="p-6 space-y-6" data-testid="migration-tools-body">
              {/* Índice de Storage */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2"><Database className="w-4 h-4" /> Índice de Storage</h4>
                <div className="flex flex-wrap gap-6 text-sm">
                  <div><span className="text-slate-500">Objetos indexados:</span> <span data-testid="migration-storage-count" className="font-bold">{info?.storage_index_count ?? "—"}</span></div>
                  <div><span className="text-slate-500">Último backfill:</span> <span className="font-semibold">{info?.last_backfill_run?.timestamp ? new Date(info.last_backfill_run.timestamp).toLocaleString() : "nunca"}</span></div>
                </div>
                {backfill && backfill.status && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm" data-testid="migration-backfill-status">
                    Estado: <span className="font-semibold">{backfill.status}</span>
                    {" · "}docs: {backfill.scanned_documents ?? 0} · paths: {backfill.paths_found ?? 0} · indexados: {backfill.indexed ?? 0} · ya estaban: {backfill.already_present ?? 0} · fallidos: {backfill.failed ?? 0}
                  </div>
                )}
                <div className="flex gap-3">
                  <button data-testid="migration-backfill-btn" onClick={() => runBackfill(false)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 hover:bg-slate-50">
                    <RefreshCw className="w-4 h-4" /> Actualizar índice
                  </button>
                  <button data-testid="migration-rebuild-btn" onClick={() => runBackfill(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-red-300 text-red-700 hover:bg-red-50">
                    <RefreshCw className="w-4 h-4" /> Reconstruir desde cero
                  </button>
                </div>
              </div>

              {/* Cleanup de duplicados */}
              <div className="space-y-3 border-t border-slate-100 pt-6" data-testid="migration-cleanup-section">
                <h4 className="font-semibold text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Cleanup de duplicados</h4>
                <div data-testid="cleanup-warning-banner" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span>Solo usar después de una migración. Detecta documentos duplicados por su nombre, conserva el más referenciado y borra los huérfanos. No usar en operación normal.</span>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500">Colecciones a procesar</label>
                  <div className="flex flex-wrap gap-5 mt-2">
                    {Object.keys(cleanupColls).map((c) => (
                      <label key={c} className="flex items-center gap-2 text-sm">
                        <input data-testid={`cleanup-coll-${c}`} type="checkbox" checked={cleanupColls[c]}
                          onChange={(e) => setCleanupColls((p) => ({ ...p, [c]: e.target.checked }))} />
                        <span className="font-mono">{c}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input data-testid="cleanup-force-check" type="checkbox" checked={cleanupForce}
                    onChange={(e) => { setCleanupForce(e.target.checked); setCleanupDidDry(false); }} />
                  <span className="inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5 text-red-600" /> Forzar borrado aunque tengan referencias (peligroso)</span>
                </label>
                {cleanupForce && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800" data-testid="cleanup-force-warning">
                    Con force activado se borrará el duplicado con MENOS referencias aunque esté en uso. Puede dejar referencias huérfanas. Usar solo si sabes lo que haces.
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <button data-testid="cleanup-dryrun-btn" onClick={() => runCleanup(true)} disabled={cleanupLoading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 hover:bg-slate-50">
                    {cleanupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Analizar (dry-run)
                  </button>
                  <button data-testid="cleanup-execute-btn" onClick={() => runCleanup(false)} disabled={cleanupLoading || !cleanupDidDry}
                    title={!cleanupDidDry ? "Primero haz un análisis dry-run" : ""}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    <Trash2 className="w-4 h-4" /> Ejecutar cleanup
                  </button>
                </div>

                {cleanupResult && (
                  <div className="space-y-3" data-testid="cleanup-result">
                    <div className={`text-sm font-semibold ${cleanupResult.dry_run ? "text-slate-700" : "text-emerald-800"}`}>
                      {cleanupResult.dry_run ? "Análisis (dry-run) — nada se borró" : "Cleanup ejecutado"}
                      {cleanupResult.force_delete_unreferenced && <span className="ml-2 text-red-700">(force ON)</span>}
                    </div>
                    {Object.keys(cleanupResult.errors || {}).length > 0 && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
                        {Object.entries(cleanupResult.errors).map(([k, v]) => <div key={k}><b>{k}</b>: {v}</div>)}
                      </div>
                    )}
                    {Object.entries(cleanupResult.summary || {}).map(([coll, s]) => (
                      <div key={coll} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`cleanup-summary-${coll}`}>
                        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-sm font-semibold font-mono flex flex-wrap gap-x-4 gap-y-1">
                          <span>{coll}</span>
                          <span className="text-slate-500 font-sans font-normal">key: {s.key_field}</span>
                          <span className="text-slate-500 font-sans font-normal">escaneados: {s.scanned}</span>
                          <span className="text-slate-500 font-sans font-normal">grupos dup: {s.groups_with_duplicates}</span>
                          <span className="text-emerald-700 font-sans font-normal">conservar: {s.would_keep}</span>
                          <span className="text-red-700 font-sans font-normal">{cleanupResult.dry_run ? `a borrar: ${s.would_delete}` : `borrados: ${s.deleted}`}</span>
                          {s.aborted_due_to_refs > 0 && <span className="text-amber-700 font-sans font-normal">abortados: {s.aborted_due_to_refs}</span>}
                        </div>
                        {s.details?.length > 0 && (
                          <table className="w-full text-xs">
                            <thead className="bg-white border-b border-slate-100">
                              <tr>
                                <th className="text-left px-3 py-1.5 font-bold text-slate-500 uppercase">Key</th>
                                <th className="text-left px-3 py-1.5 font-bold text-slate-500 uppercase">Conservado (refs)</th>
                                <th className="text-left px-3 py-1.5 font-bold text-slate-500 uppercase">Borrar (refs)</th>
                                <th className="px-3 py-1.5"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.details.map((g, i) => (
                                <tr key={i} className="border-b border-slate-50 last:border-0 align-top">
                                  <td className="px-3 py-1.5 font-mono">{g.key}</td>
                                  <td className="px-3 py-1.5 font-mono text-emerald-700">{g.kept?.id?.slice(0, 12)}… ({g.kept?.refs_count})</td>
                                  <td className="px-3 py-1.5 font-mono text-red-600">
                                    {(g.deleted || []).map((d, j) => <div key={j}>{d.id?.slice(0, 12)}… ({d.refs_count})</div>)}
                                  </td>
                                  <td className="px-3 py-1.5">
                                    {g.aborted && <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><ShieldAlert className="w-3.5 h-3.5" /> abortado (en uso)</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Remap de referencias */}
              <div className="space-y-3 border-t border-slate-100 pt-6" data-testid="migration-remap-section">
                <h4 className="font-semibold text-sm flex items-center gap-2"><ArrowLeftRight className="w-4 h-4" /> Remapear referencias (FK)</h4>
                <div data-testid="remap-warning-banner" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span>Actualiza un campo (ej. branch_id) en otra colección según un mapping viejo→nuevo. Útil cuando el cleanup conservó un id distinto al que apuntan las OCs/recepciones/etc. Soporta strings, arrays (branches_authorized) y paths anidados (meta.branch_id).</span>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input data-testid="remap-bulk-check" type="checkbox" checked={remapBulk}
                    onChange={(e) => { setRemapBulk(e.target.checked); setRemapDidDry(false); }} />
                  <span>Bulk — aplicar a TODAS las colecciones que tengan ese campo</span>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {!remapBulk && (
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Colección</label>
                      <input data-testid="remap-collection-input" value={remapCollection} onChange={(e) => { setRemapCollection(e.target.value); setRemapDidDry(false); }}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono" placeholder="purchase_orders" />
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-semibold text-slate-500">Campo (dot-notation permitido)</label>
                    <input data-testid="remap-field-input" value={remapField} onChange={(e) => { setRemapField(e.target.value); setRemapDidDry(false); }}
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono" placeholder="branch_id" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500">Mapping JSON {`{ "id_viejo": "id_nuevo", ... }`}</label>
                  <textarea data-testid="remap-mapping-input" value={remapMapping} onChange={(e) => { setRemapMapping(e.target.value); setRemapDidDry(false); }}
                    rows={5} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-xs font-mono"
                    placeholder='{"e3798873-...": "7e4bf55a-...", "3cbfa775-...": "3679c6f6-..."}' />
                </div>

                <div className="flex flex-wrap gap-3">
                  <button data-testid="remap-dryrun-btn" onClick={() => runRemap(true)} disabled={remapLoading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 hover:bg-slate-50">
                    {remapLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Analizar (dry-run)
                  </button>
                  <button data-testid="remap-execute-btn" onClick={() => runRemap(false)} disabled={remapLoading || !remapDidDry}
                    title={!remapDidDry ? "Primero haz un análisis dry-run" : ""}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    <ArrowLeftRight className="w-4 h-4" /> Ejecutar remap
                  </button>
                </div>

                {remapResult && (
                  <div className="space-y-3" data-testid="remap-result">
                    <div className={`text-sm font-semibold ${remapResult.dry_run ? "text-slate-700" : "text-emerald-800"}`}>
                      {remapResult.dry_run ? "Análisis (dry-run) — nada se modificó" : "Remap ejecutado"}
                      {typeof remapResult.total_updated === "number" && <span className="ml-2 text-slate-500">· total actualizados: {remapResult.total_updated}</span>}
                    </div>
                    {(remapResult.summary
                      ? Object.entries(remapResult.summary).map(([coll, s]) => ({ coll, ...s }))
                      : [{ coll: remapResult.collection, scanned: remapResult.scanned, updated: remapResult.updated, unchanged: remapResult.unchanged, examples: remapResult.examples }]
                    ).map((s) => (
                      <div key={s.coll} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`remap-summary-${s.coll}`}>
                        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-sm font-mono flex flex-wrap gap-x-4 gap-y-1">
                          <span className="font-semibold">{s.coll}</span>
                          <span className="text-slate-500 font-sans">escaneados: {s.scanned}</span>
                          <span className="text-emerald-700 font-sans">{remapResult.dry_run ? `a actualizar: ${s.updated}` : `actualizados: ${s.updated}`}</span>
                          <span className="text-slate-500 font-sans">sin cambio: {s.unchanged}</span>
                        </div>
                        {s.examples?.length > 0 && (
                          <div className="px-4 py-2 text-xs font-mono space-y-1">
                            {s.examples.slice(0, 8).map((ex, i) => (
                              <div key={i} className="text-slate-600">{String(ex.doc_id).slice(0, 14)}…: <span className="text-red-600">{JSON.stringify(ex.from)}</span> → <span className="text-emerald-700">{JSON.stringify(ex.to)}</span></div>
                            ))}
                            {s.examples.length > 8 && <div className="text-slate-400">… y {s.examples.length - 8} más</div>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
